import type { Router } from 'vue-router';

import dayjs from 'dayjs';

import {
  adsSources,
  invalidVisionUgcSources,
  sourceTypeEnum,
  visionPredictedSources,
  visualSources,
} from '@/app/library/constants';
import { PREDICTION_MODEL } from '@/config';
import { useAuthStore } from '@/stores/auth';

type Rating = 'excellent' | 'good' | 'bad';
type PredictionTier = 'top' | 'better' | 'less';
type PerformanceTier = 'top' | 'high' | 'low';

interface PredictionMedia {
  source?: unknown;
  sourceType?: unknown;
  [key: string]: unknown;
}

interface PredictionModel {
  field: string;
  v2field: string;
}

interface GetPredictionRatingOptions {
  currentModel?: PredictionModel;
  mediaV2?: boolean;
}

type MediaPredictions = Record<string, unknown> | 'processing' | null | undefined;

// Routes where prediction badges are suppressed regardless of brand/source. Empty now: legacy 'instagram.galleries' (#4469) removed in sc-197870 since that layout is gone and it was hiding predictions in the Select Media drawer.
export const EXCLUDED_ROUTES: string[] = [];

export const engagementRating = {
  good: 0.5,
  excellent: 0.75,
};

export const day = {
  today: 'Today',
  yesterday: 'Yesterday',
  lastSevenDays: 'Last 7 Days',
  last30Days: 'Last 30 Days',
};

export function getRating(value: number | null | undefined): Rating | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (value > engagementRating.excellent) {
    return 'excellent';
  }
  if (value > engagementRating.good) {
    return 'good';
  }
  return 'bad';
}

export function getVisionPredictionValue(
  prediction: number | null | undefined,
): PredictionTier | null {
  if (prediction === null || prediction === undefined) {
    return null;
  }
  if (prediction > engagementRating.excellent) {
    return 'top';
  }
  if (prediction > engagementRating.good) {
    return 'better';
  }
  return 'less';
}

export function getPredictionRating(
  predictions: MediaPredictions,
  { currentModel = PREDICTION_MODEL.ENGAGEMENT, mediaV2 = false }: GetPredictionRatingOptions = {},
): Rating | 'processing' | null {
  if (!predictions || !currentModel) return null;
  const field = mediaV2 ? currentModel.v2field : currentModel.field;
  if (predictions === 'processing') {
    return 'processing';
  }
  const value = predictions[field];
  if (value === 'processing') {
    return 'processing';
  }
  return getRating(value as number | null | undefined);
}

const PREDICTION_TIER_RANGES: Record<PerformanceTier, { from: number; to: number }> = Object.freeze(
  {
    top: { from: engagementRating.excellent, to: 1 },
    high: { from: engagementRating.good, to: engagementRating.excellent },
    low: { from: 0, to: engagementRating.good },
  },
);

// Legacy single-string value from the radio era; expanded into tiers so old URLs/saved filters keep working.
function expandLegacyTier(tier: string): string[] {
  return tier === 'top_high' ? ['top', 'high'] : [tier];
}

export function getPerformanceParameters(value?: string | string[] | null): {
  from: number | null;
  to: number | null;
} {
  // Accepts a single tier string (legacy) or array of tiers (sc-197870); returns union {from, to}. Empty/null → {from: null, to: null} ("no filter").
  if (value === undefined || value === null) {
    return { from: null, to: null };
  }
  const tiers = (Array.isArray(value) ? value : [value])
    .flatMap(expandLegacyTier)
    .filter((tier): tier is PerformanceTier => tier in PREDICTION_TIER_RANGES);
  if (tiers.length === 0) {
    return { from: null, to: null };
  }
  const ranges = tiers.map((tier) => PREDICTION_TIER_RANGES[tier]);
  return {
    from: Math.min(...ranges.map((r) => r.from)),
    to: Math.max(...ranges.map((r) => r.to)),
  };
}

export function showPredictions(media?: PredictionMedia | null, router?: Router | null): boolean {
  const authStore = useAuthStore();
  // Check existence of required properties
  if (!media) return false;
  const sourceType = media.sourceType as string | undefined;
  const source = media.source as string | undefined;
  const sourceTypeParts = sourceType?.split(':');
  const mediaSource = sourceTypeParts ? sourceTypeParts[sourceTypeParts.length - 1] : source;
  if (!mediaSource) return false;

  // Check base permissions
  if (!authStore.user_can('vision', 'can_access_vision')) return false;

  // Source exclusions: UGC permissive (any source not in invalidVisionUgcSources); OWNED strict — bare 'OWNED' qualifies only when media.source is a vision source (UPLOAD/EDITOR/AI_GENERATION).
  const isVisionPredictedSource =
    visionPredictedSources.includes(mediaSource) ||
    (mediaSource === sourceTypeEnum.UGC && !invalidVisionUgcSources.includes(source as string)) ||
    (mediaSource === sourceTypeEnum.OWNED && visionPredictedSources.includes(source as string));
  if (!isVisionPredictedSource && !Object.values(visualSources).includes(mediaSource)) {
    return false;
  }
  if (Object.values(adsSources).includes(mediaSource)) return false;

  // Route exclusions
  const routeName = router?.currentRoute?.value?.name;
  if (typeof routeName === 'string' && EXCLUDED_ROUTES.find((r) => routeName.startsWith(r))) {
    return false;
  }
  // Normal users show predictions on vision prediction sources
  return isVisionPredictedSource;
}

export function formatDate(date: string | number | Date): string {
  const formatType = 'dddd, MMMM Do';
  const currDate = dayjs().format(formatType);
  const yesterdaysDate = dayjs().subtract(1, 'day').format(formatType);
  let formattedDate = dayjs(new Date(date)).format(formatType);
  if (formattedDate === currDate) {
    formattedDate = day.today;
  } else if (formattedDate === yesterdaysDate) {
    formattedDate = day.yesterday;
  }
  return formattedDate;
}

export function formatTimestamp(timeCreated: string | number | Date): string {
  return dayjs(new Date(timeCreated)).format('h:mm a');
}
